import {
  type InfrastructureCommand,
  InfrastructureCommandBuilder,
  type InfrastructureResponse,
  InfrastructureResponseBuilder,
} from '@teachino/shared-infrastructure';
import type {
  CourseTopicsFacade,
  SortOrder,
  ViewModel,
} from './course-topics.facade';

import { useMutation, useQuery } from '@urql/vue';
import { computed, ref } from 'vue';

import type { DateRange } from '@teachino/shared-design-system';
import { useDateFormat } from '@teachino/shared-util';
import {
  convertTopicListItemFragment,
  type TopicListItem,
} from '../folders/topic-list-item';
import { removeCourseTopicMutation } from '../remove-course-topic.mutation';
import { reorderCourseTopicMutation } from '../reorder-course-topic.mutation';
import { convertCourseFragment } from '../topic/course.fragment';
import { convertCourseTopicFragment } from './course-topic.fragment';
import { createTopicInCourseMutation } from './create-topic-in-course.mutation';
import { topicInstancesQuery } from './topic-instances.query';
import { updateTopicInstanceMutation } from './update-topic-instance.mutation';

export class CourseTopicsService implements CourseTopicsFacade {
  private _courseId = ref<string | null>(null);
  private _sortOrder = ref<SortOrder>('default');

  private _reorderCourseTopic = useMutation(reorderCourseTopicMutation);
  private _removeCourseTopic = useMutation(removeCourseTopicMutation);

  private _createTopicInCourse = useMutation(createTopicInCourseMutation);

  private _updateTopicInstance = useMutation(updateTopicInstanceMutation);

  private _topicInstancesQuery = useQuery({
    query: topicInstancesQuery,
    variables: computed(() => {
      return { courseId: this._courseId.value ?? '' };
    }),
    pause: computed(() => this._courseId.value === null),
  });

  vm(): InfrastructureResponse<ViewModel> {
    return InfrastructureResponseBuilder.fromQuery(this._topicInstancesQuery)
      .withMapper((result) => {
        return {
          topics: this.sortTopics({
            topics: result.topicInstances.map((topicInstance) =>
              convertCourseTopicFragment(topicInstance),
            ),
            sortOrder: this._sortOrder.value,
          }),
          course: convertCourseFragment(result.course),
        };
      })
      .build();
  }

  selectCourse(courseId: string) {
    this._courseId.value = courseId;
    this.reloadData();
  }

  selectSortOrder(sortOrder: SortOrder) {
    this._sortOrder.value = sortOrder;
  }

  private sortTopics(args: {
    topics: ViewModel['topics'];
    sortOrder: SortOrder;
  }): ViewModel['topics'] {
    const sortAlphabetically = (topics: ViewModel['topics']) => {
      return topics.sort((a, b) => {
        if (!a.name || !b.name) return 0;
        return a.name.localeCompare(b.name);
      });
    };
    const sortByDate = (topics: ViewModel['topics']) => {
      return topics.sort((a, b) => {
        // Check if arrays are empty and put them at the end
        if (!a.range?.start && !b.range?.start) return 0;
        if (!a.range?.start) return 1;
        if (!b.range?.start) return -1;

        const res = a.range.start < b.range.start;
        return res ? 1 : -1;
      });
    };
    const sortByDefault = (topics: ViewModel['topics']) => topics;
    const sortMap: Record<
      SortOrder,
      (items: ViewModel['topics']) => ViewModel['topics']
    > = {
      alphabetically: sortAlphabetically,
      date: sortByDate,
      default: sortByDefault,
    };
    return sortMap[args.sortOrder](args.topics);
  }

  reloadData(): void {
    this._topicInstancesQuery.executeQuery();
  }

  reorderCourseTopic(topicInstanceId: string, position: number) {
    return InfrastructureCommandBuilder.fromMutation(this._reorderCourseTopic, {
      reorderTopicInstanceInput: {
        id: topicInstanceId,
        position,
      },
    }).build();
  }

  removeCourseTopics(topicInstanceIds: string[]) {
    return InfrastructureCommandBuilder.fromMutation(this._removeCourseTopic, {
      topicInstanceIds,
      forceDelete: true,
    })
      .withSideeffect(() => {
        this.reloadData();
      })
      .build();
  }

  updateDateRange(topicInstanceId: string, range: DateRange | null) {
    const { dateToDateString } = useDateFormat();
    return InfrastructureCommandBuilder.fromMutation(
      this._updateTopicInstance,
      {
        updateTopicInstanceInput: {
          id: topicInstanceId,
          startDate: range ? dateToDateString(range.start) : null,
          endDate: range ? dateToDateString(range.end) : null,
        },
      },
    )
      .withSideeffect(() => {
        this.reloadData();
      })
      .build();
  }

  createTopicInCourse(
    courseId: string,
    name: string,
    goals: string | null,
  ): InfrastructureCommand<TopicListItem> {
    return InfrastructureCommandBuilder.fromMutation(
      this._createTopicInCourse,
      {
        name,
        courseId,
        goals,
      },
    )
      .withMapper((result) => {
        return convertTopicListItemFragment(
          result.createTopicInstance.topicInstance.topic,
        );
      })
      .withSideeffect(() => {
        this.reloadData();
      })
      .build();
  }
}
